/**
 * Расстояния от выделенного предмета — то, что показывают размеры при зажатом Ctrl/Cmd.
 *
 *   • До стен (`itemClearances`, `wallFixtureClearances`): от каждой стороны предмета пускаем
 *     луч наружу до грани чистого контура комнаты, где он стоит, — так меряют рулеткой.
 *     Предмет не в комнате — стены ищем по контурам всех стен. Сторона вплотную — без размера.
 *     Предмет на полу меряется вдоль своих осей (повёрнутый диван — вдоль своих сторон),
 *     навесной — вдоль стены до углов и от лицевой стороны вперёд, в комнату.
 *     Дверь и окно (`openingClearances`) — вдоль стены от краёв проёма до углов с обеих сторон
 *     стены: так проём ставят «в 300 от угла».
 *   • От точки черчения стены (`pointClearances`) — лучами в заданные стороны до граней
 *     стен; точка на оси стены (`wallAxisAt`) меряется вдоль этой стены до её концов.
 *   • До другого предмета (`gapClearances`) — просвет между ними по осям выделенного, как
 *     в Figma: по каждой оси, где между ними есть зазор. Если предметы не стоят друг напротив
 *     друга, линия идёт от середины выделенного, а к краю второго — пунктирная выноска.
 */
import {
	add,
	dot,
	leftNormal,
	mul,
	norm,
	pointInPolygon,
	sub,
	type Pt,
} from "@/shared/lib/geometry";
import type { Fixture, FloorItem, WallGraph } from "@/shared/plan";
import { fixturePlanPoint } from "./cables";
import { pointAlongWall } from "./walls";
import type { Room } from "./rooms";
import type { WallOutline } from "./wallPolygons";

/** Размер от края предмета до стены или до другого предмета. */
export interface Clearance {
	from: Pt;
	to: Pt;
	/** Выноска к краю второго предмета, если линия в него не упирается */
	guide?: [Pt, Pt];
}

/** Ближе этого, мм, — вплотную: размера нет. */
const TOUCH = 1;
/** Насколько навесной предмет меряем «от стены» вдоль неё, мм: луч чуть отступает от грани. */
const ALONG_WALL_INSET = 50;

/** Где луч `origin + dir·t` впервые пересекает отрезок p–q; null — не пересекает. */
const rayHit = (origin: Pt, dir: Pt, p: Pt, q: Pt): number | null => {
	const ex = q.x - p.x;
	const ey = q.y - p.y;
	const denom = dir.x * ey - dir.y * ex;
	if (Math.abs(denom) < 1e-9) return null;
	const wx = p.x - origin.x;
	const wy = p.y - origin.y;
	const t = (wx * ey - wy * ex) / denom;
	const u = (wx * dir.y - wy * dir.x) / denom;
	if (t < -1 || u < 0 || u > 1) return null;
	return t;
};

/** Рёбра, о которые упирается луч: стороны контуров. */
const edgesOf = (polygons: Pt[][]): [Pt, Pt][] =>
	polygons.flatMap((poly) => poly.map((p, i): [Pt, Pt] => [p, poly[(i + 1) % poly.length]]));

/** Ближайшее попадание луча в рёбра; Infinity — ни во что не упёрся. */
const nearestHit = (from: Pt, dir: Pt, edges: [Pt, Pt][]) => {
	let nearest = Infinity;
	for (const [p, q] of edges) {
		const t = rayHit(from, dir, p, q);
		if (t !== null && t < nearest) nearest = Math.max(0, t);
	}
	return nearest;
};

/** Стены вокруг точки: чистый контур её комнаты, а вне комнат — контуры всех стен. */
const wallEdgesAround = (point: Pt, rooms: Room[], outlines: Record<string, WallOutline>) => {
	const room = rooms.find((candidate) => pointInPolygon(point, candidate.inner));
	return edgesOf(room ? [room.inner] : Object.values(outlines));
};

/** Пускает лучи и собирает размеры до стен. */
const castToWalls = (rays: { from: Pt; dir: Pt }[], wallEdges: [Pt, Pt][]): Clearance[] =>
	rays.flatMap(({ from, dir }) => {
		const wall = nearestHit(from, dir, wallEdges);
		return Number.isFinite(wall) && wall >= TOUCH ? [{ from, to: add(from, mul(dir, wall)) }] : [];
	});

/** Контур предмета на полу — прямоугольник габарита, повёрнутый вместе с ним. */
export const floorItemFootprint = (
	item: FloorItem,
	size: { width: number; depth: number },
): Pt[] => {
	const angle = (item.rotation * Math.PI) / 180;
	const right = { x: Math.cos(angle), y: Math.sin(angle) };
	const forward = { x: -Math.sin(angle), y: Math.cos(angle) };
	const w = size.width / 2;
	const d = size.depth / 2;
	return [
		[-w, -d],
		[w, -d],
		[w, d],
		[-w, d],
	].map(([u, v]) => ({
		x: item.x + right.x * u + forward.x * v,
		y: item.y + right.y * u + forward.y * v,
	}));
};

/** Оси навесного предмета: точка на грани стены, направление вдоль неё и в комнату. */
const fixtureFrame = (graph: WallGraph, fixture: Fixture) => {
	const wall = graph.walls[fixture.wallId];
	const a = wall ? graph.nodes[wall.a] : undefined;
	const b = wall ? graph.nodes[wall.b] : undefined;
	const face = fixturePlanPoint(graph, fixture);
	if (!a || !b || !face) return null;
	const along = norm(sub(b, a));
	const into = mul(leftNormal(along), fixture.side);
	return { face, along, into };
};

/** Контур навесного предмета на плане: ширина вдоль стены, глубина — в комнату. */
export const wallFixtureFootprint = (
	graph: WallGraph,
	fixture: Fixture,
	size: { width: number; depth: number },
): Pt[] | null => {
	const frame = fixtureFrame(graph, fixture);
	if (!frame) return null;
	const { face, along, into } = frame;
	const half = mul(along, size.width / 2);
	const front = mul(into, size.depth);
	return [
		sub(face, half),
		add(face, half),
		add(add(face, half), front),
		add(sub(face, half), front),
	];
};

/** Размеры от предмета на полу до стен — вдоль его осей. */
export const itemClearances = (
	item: FloorItem,
	size: { width: number; depth: number },
	rooms: Room[],
	outlines: Record<string, WallOutline>,
): Clearance[] => {
	const center = { x: item.x, y: item.y };
	const angle = (item.rotation * Math.PI) / 180;
	const right = { x: Math.cos(angle), y: Math.sin(angle) };
	const forward = { x: -Math.sin(angle), y: Math.cos(angle) };
	const rays = [
		{ dir: right, half: size.width / 2 },
		{ dir: mul(right, -1), half: size.width / 2 },
		{ dir: forward, half: size.depth / 2 },
		{ dir: mul(forward, -1), half: size.depth / 2 },
	].map(({ dir, half }) => ({ from: add(center, mul(dir, half)), dir }));
	return castToWalls(rays, wallEdgesAround(center, rooms, outlines));
};

/**
 * Размеры от навесного предмета до стен: вдоль стены в обе стороны — до углов
 * (розетка: «45 см от угла»), и от лицевой стороны вперёд, в комнату.
 */
export const wallFixtureClearances = (
	graph: WallGraph,
	fixture: Fixture,
	size: { width: number; depth: number },
	rooms: Room[],
	outlines: Record<string, WallOutline>,
): Clearance[] => {
	const frame = fixtureFrame(graph, fixture);
	if (!frame) return [];
	const { face, along, into } = frame;
	// Вдоль стены луч идёт чуть в стороне от её грани — иначе он скользит по самой грани
	const inset = add(face, mul(into, Math.min(size.depth / 2, ALONG_WALL_INSET)));
	const rays = [
		{ from: add(inset, mul(along, size.width / 2)), dir: along },
		{ from: sub(inset, mul(along, size.width / 2)), dir: mul(along, -1) },
		{ from: add(face, mul(into, size.depth)), dir: into },
	];
	return castToWalls(rays, wallEdgesAround(inset, rooms, outlines));
};

/**
 * Размеры от двери или окна до углов: от каждого края проёма вдоль стены до ближайшей стены
 * поперёк — по обеим граням стены, ведь проём видно из обеих комнат. Проём задан своим
 * местом на стене, а не объектом: так меряют и призрак, который ещё не поставлен.
 */
export const openingClearances = (
	graph: WallGraph,
	opening: { wallId: string; offset: number; width: number },
	rooms: Room[],
	outlines: Record<string, WallOutline>,
): Clearance[] => {
	const wall = graph.walls[opening.wallId];
	const a = wall ? graph.nodes[wall.a] : undefined;
	const b = wall ? graph.nodes[wall.b] : undefined;
	if (!wall || !a || !b) return [];
	const along = norm(sub(b, a));
	const center = pointAlongWall(a, b, opening.offset);
	return ([1, -1] as const).flatMap((side) => {
		// Луч идёт чуть в стороне от грани стены — иначе он скользит по самой грани
		const inset = add(
			center,
			mul(leftNormal(along), side * (wall.thickness / 2 + ALONG_WALL_INSET)),
		);
		const rays = [
			{ from: add(inset, mul(along, opening.width / 2)), dir: along },
			{ from: sub(inset, mul(along, opening.width / 2)), dir: mul(along, -1) },
		];
		return castToWalls(rays, wallEdgesAround(inset, rooms, outlines));
	});
};

/**
 * Стена, на оси которой лежит точка (Т-стык), и направление оси от `a` к `b`; null — точка
 * не на оси или в её конце (в узле меряют не вдоль стены).
 */
export const wallAxisAt = (
	graph: WallGraph,
	point: Pt,
): { along: Pt; thickness: number } | null => {
	for (const wall of Object.values(graph.walls)) {
		const a = graph.nodes[wall.a];
		const b = graph.nodes[wall.b];
		if (!a || !b) continue;
		const axis = sub(b, a);
		const length = Math.hypot(axis.x, axis.y);
		if (length < TOUCH) continue;
		const along = norm(axis);
		const offset = sub(point, a);
		const t = dot(offset, along);
		const across = Math.abs(offset.x * along.y - offset.y * along.x);
		if (across <= TOUCH && t > TOUCH && t < length - TOUCH) {
			return { along, thickness: wall.thickness };
		}
	}
	return null;
};

/**
 * Размеры от точки до стен — лучами в стороны `directions` до первой грани стены (по контурам
 * всех стен). Луч, ни во что не упёршийся, размера не даёт. Точка на оси стены лежит внутри её
 * контура: луч вдоль оси упирается в её же торец или стык в углу — это и есть «до конца стены».
 */
export const pointClearances = (
	point: Pt,
	directions: Pt[],
	outlines: Record<string, WallOutline>,
): Clearance[] =>
	castToWalls(
		directions.map((dir) => ({ from: point, dir })),
		edgesOf(Object.values(outlines)),
	);

/** Габарит контура в осях `u`, `v`. */
const extent = (poly: Pt[], u: Pt, v: Pt) => {
	const us = poly.map((p) => dot(p, u));
	const vs = poly.map((p) => dot(p, v));
	return {
		minU: Math.min(...us),
		maxU: Math.max(...us),
		minV: Math.min(...vs),
		maxV: Math.max(...vs),
	};
};

/**
 * Просвет между выделенным предметом `self` и другим `other` (контуры на плане) — по осям
 * выделенного: оси берём по первой стороне его контура (у прямоугольника габарита — ширина).
 * Предметы заходят друг на друга по обеим осям — размеров нет.
 */
export const gapClearances = (self: Pt[], other: Pt[]): Clearance[] => {
	const u = norm(sub(self[1], self[0]));
	const v = leftNormal(u);
	const at = (a: number, b: number): Pt => add(mul(u, a), mul(v, b));
	const s = extent(self, u, v);
	const o = extent(other, u, v);
	const result: Clearance[] = [];

	/**
	 * Зазор по одной оси: `along` — позиции на этой оси, `across` — на другой; `point` строит
	 * точку плана из (вдоль, поперёк).
	 */
	const gap = (
		selfLo: number,
		selfHi: number,
		otherLo: number,
		otherHi: number,
		selfAcrossLo: number,
		selfAcrossHi: number,
		otherAcrossLo: number,
		otherAcrossHi: number,
		point: (along: number, across: number) => Pt,
	) => {
		let from: number;
		let to: number;
		if (otherLo - selfHi >= TOUCH) [from, to] = [selfHi, otherLo];
		else if (selfLo - otherHi >= TOUCH) [from, to] = [selfLo, otherHi];
		else return;
		const lo = Math.max(selfAcrossLo, otherAcrossLo);
		const hi = Math.min(selfAcrossHi, otherAcrossHi);
		if (lo <= hi) {
			// Стоят друг напротив друга — линия посередине общего участка
			const across = (lo + hi) / 2;
			result.push({ from: point(from, across), to: point(to, across) });
			return;
		}
		// Наискосок — линия от середины выделенного, выноска к ближнему краю второго
		const across = (selfAcrossLo + selfAcrossHi) / 2;
		const edge = otherAcrossLo > selfAcrossHi ? otherAcrossLo : otherAcrossHi;
		result.push({
			from: point(from, across),
			to: point(to, across),
			guide: [point(to, across), point(to, edge)],
		});
	};

	gap(s.minU, s.maxU, o.minU, o.maxU, s.minV, s.maxV, o.minV, o.maxV, (a, b) => at(a, b));
	gap(s.minV, s.maxV, o.minV, o.maxV, s.minU, s.maxU, o.minU, o.maxU, (a, b) => at(b, a));
	return result;
};
